import {TrustAccountMemberEventType} from '@sollapay/enums';
import {Typography} from '@sollapay/ui';
import {Dot} from '@sollapay/ui/components';
import {formatDayMonthTimeLocalized} from '@sollapay/utils';
import {Trans, useTranslation} from 'react-i18next';

import {RetryAlert} from '@/components';

import {ShareChangeLogSkeleton} from './ShareChangeLog.skeleton';
import {getGuestDisplayName} from '../utils';

import type {ShareLogEntry} from '../utils';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

const {
  ADDED,
  ROLE_CHANGED,
  POWER_OF_ATTORNEY_GRANTED,
  INVITED,
  INVITATION_RESENT,
  INVITATION_REVOKED,
  REMOVED
} = TrustAccountMemberEventType;

const ENDING_EVENTS: ReadonlySet<TrustAccountMemberEventType> = new Set([
  REMOVED,
  INVITATION_REVOKED
]);

const WORDED_EVENTS: Record<ShareLogEntry['kind'], ReadonlySet<TrustAccountMemberEventType>> = {
  member: new Set([ADDED, ROLE_CHANGED, REMOVED, POWER_OF_ATTORNEY_GRANTED]),
  guest: new Set([INVITED, ADDED, INVITATION_RESENT, INVITATION_REVOKED, REMOVED])
};

export interface ShareChangeLogProps {
  entries: ShareLogEntry[];
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

/**
 * Who joined, changed role or lost access to the account, team and guests together, newest first.
 * When a log failed to load it says so, rather than claiming nothing changed.
 */
export const ShareChangeLog: FC<ShareChangeLogProps> = ({
  entries,
  isLoading = false,
  hasError = false,
  onRetry
}) => {
  const {t} = useTranslation('trustAccounts');

  if (isLoading) return <ShareChangeLogSkeleton />;

  return (
    <div className="flex flex-col gap-2">
      {hasError && <RetryAlert description={t('share.log.error')} onRetry={onRetry} />}
      {!entries.length && !hasError && (
        <Typography size="sm" color="tertiary" className="py-6 text-center whitespace-normal">
          {t('share.log.empty')}
        </Typography>
      )}
      {entries.length > 0 && (
        <ul
          data-slot="share-change-log"
          aria-label={t('share.log.label')}
          className="flex max-h-100 flex-col divide-y divide-border-subtle overflow-y-auto"
        >
          {entries.map(entry => (
            <ShareLogItem key={`${entry.kind}-${entry.event.id}`} entry={entry} />
          ))}
        </ul>
      )}
    </div>
  );
};

const ShareLogItem: FC<{entry: ShareLogEntry}> = ({entry}) => {
  const {t, i18n} = useTranslation('trustAccounts');
  const {kind, event} = entry;
  const wording = WORDED_EVENTS[kind].has(event.type) ? event.type : 'other';

  return (
    <li className="flex gap-2.5 py-2.5">
      <Dot
        className="mt-1.5"
        colorClassName={ENDING_EVENTS.has(event.type) ? 'bg-indication-error' : 'bg-brand-accent'}
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <Typography size="sm" className="whitespace-normal">
          <Trans
            ns="trustAccounts"
            i18nKey={`share.log.${kind}.${wording}`}
            values={getEntryValues(entry, t)}
            components={{b: <strong />}}
          />
        </Typography>
        <Typography size="xs" color="tertiary">
          {formatDayMonthTimeLocalized(event.createdAt, i18n.language)}
        </Typography>
      </span>
    </li>
  );
};

function getEntryValues({kind, event}: ShareLogEntry, t: TFunction): Record<string, string> {
  const unknown = t('share.log.unknownUser');
  const actor = event.actorName ?? unknown;
  if (kind === 'guest') {
    return {
      actor,
      subject: getGuestDisplayName({name: event.subjectName, email: event.subjectEmail})
    };
  }
  const {fromRole, toRole} = event;
  const to = toRole ? t(`share.roles.${toRole}.label`) : '';
  return {
    actor,
    subject: event.subjectName ?? unknown,
    role: to,
    from: fromRole ? t(`share.roles.${fromRole}.label`) : '',
    to
  };
}
